<template>
  <v-data-table
    :headers="columnsWithExpand"
    :items="visibleItems"
    class="w-100"
    hide-default-footer
    item-key="id"
    :items-per-page="-1">
    <template #item.internalCode="{ item }">
      <div
        :style="{ marginLeft: `${item.level * 20}px` }"
        class="d-flex align-center">
        <v-btn
          v-if="item.childItems?.length"
          density="compact"
          icon
          variant="text"
          @click="toggle(item.id)">
          <v-icon color="grey" size="small">
            {{ isExpanded(item.id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
          </v-icon>
        </v-btn>
        <div>
          {{ item.internalCode }}
        </div>
      </div>
    </template>
    <template #item.name="{ item }">
      {{ item.name }}
    </template>
    <template #item.departmentType="{ item }">
      {{ item.departmentType?.name }}
    </template>
    <template #item.status="{ item }">
      <v-chip :color="item.isActive ? 'success' : 'error'" variant="tonal">
        {{ item.isActive ? 'Діючий' : 'Заблокований' }}
      </v-chip>
    </template>

    <template #item.actions="{ item }">
      <b-action-menu
        :actions="[
          {
            title: 'Додати дочірній підрозділ',
            icon: 'mdi-plus',
            action: () => onCreateChild(item),
            iconProps: {
              color: 'blue-darken-3',
            },
          },
          {
            title: 'Редагувати',
            icon: 'mdi-pencil',
            action: () => onEdit(item),
            iconProps: {
              color: 'blue-darken-3',
            },
          },
          {
            title: 'Видалити',
            icon: 'mdi-delete-forever',
            action: () => onDelete(item),
            listItemProps: {
              class: 'text-error',
            },
            iconProps: {
              color: 'error',
            },
          },
        ]">
      </b-action-menu>
    </template>
  </v-data-table>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { openDialog } from '@/dialog'
import { BActionMenu } from 'best-modules/components'
import { internalDepartmentDelete } from '@/requests/internalDepartment'
import { DataTableHeader } from '@/utils/types/vuetify'

interface Item {
  id: number
  internalCode: string
  name: string
  departmentType?: { name: string }
  isActive: boolean
  childItems?: Item[]
}

type FlatItem = Item & {
  level: number
  parentId: number | null
}

const props = defineProps<{
  items: Item[]
  columns: DataTableHeader[]
  openEditDialog: (_item: Item) => void
}>()

const emit = defineEmits(['refresh'])

const expanded = ref<Set<number>>(new Set())

const toggle = (id: number) => {
  if (expanded.value.has(id)) expanded.value.delete(id)
  else expanded.value.add(id)
}

const isExpanded = (id: number) => expanded.value.has(id)

const columnsWithExpand = computed(() => [
  { title: 'Код', key: 'internalCode', width: '200px' },
  ...props.columns.filter(c => c.key !== 'internalCode'),
])

const flattenTree = (
  items: Item[],
  level = 0,
  parentId: number | null = null
): FlatItem[] => {
  let result: FlatItem[] = []

  for (const item of items) {
    result.push({
      ...item,
      level,
      parentId,
    })

    if (item.childItems?.length) {
      result = result.concat(flattenTree(item.childItems, level + 1, item.id))
    }
  }

  return result
}

const flatItems = computed(() => flattenTree(props.items || []))

const visibleItems = computed(() => {
  const map = new Map<number, FlatItem>()
  flatItems.value.forEach(i => map.set(i.id, i))

  return flatItems.value.filter(item => {
    let parentId = item.parentId

    while (parentId) {
      if (!expanded.value.has(parentId)) return false
      parentId = map.get(parentId)?.parentId || null
    }

    return true
  })
})

const onCreateChild = (item: Item) => {
  openDialog({
    name: 'InternalDepartment',
    action: 'create',
    params: {
      title: 'Створити дочірній підрозділ',
      submitLabel: 'Створити',
      cb: () => emit('refresh'),
    },
    dialogData: { itemData: { parentId: item.id } },
  })
}

const onEdit = (item: Item) => {
  props.openEditDialog(item)
}

const onDelete = (item: Item) => {
  internalDepartmentDelete(item.id).then(() => emit('refresh'))
}
</script>

<style lang="scss" scoped>
.parent-row > td {
  background: #f5f8ff !important;
}
</style>
